"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BufferAssessment = void 0;
var abr_types_1 = require("./abr-types");
var ts_deepmerge_1 = require("./utils/ts-deepmerge");
var defaultConfig = {
    bufferInterval: 1000,
    bufferThresholdSeconds: {
        rebuffer: 6,
        steady: 12,
    },
};
/**
 * See https://wiki.inbcu.com/pages/viewpage.action?pageId=643637504
 */
var BufferAssessment = /** @class */ (function () {
    function BufferAssessment(logger, config, lowestBufferAhead, prevBufferAhead) {
        if (lowestBufferAhead === void 0) { lowestBufferAhead = 0; }
        if (prevBufferAhead === void 0) { prevBufferAhead = 0; }
        this.lowestBufferAhead = lowestBufferAhead;
        this.prevBufferAhead = prevBufferAhead;
        this.bufferChangeListeners = [];
        this.isLowBufferExpected = true; // In some scenarios we expect the buffer to be low (eg: startup/seeking)
        this.lastStabilisedBufferAhead = 0;
        this.logger = logger.withContext('Buffer Assessment');
        this.logger.verbose('Received config: ', config);
        this.bufferAssessmentConfig = ts_deepmerge_1.merge.withOptions({ allowUndefinedOverrides: false }, defaultConfig, config);
        this.logger.info('Using config: ', this.bufferAssessmentConfig);
        this.intervalId = this.bufferRangeStateInterval();
    }
    BufferAssessment.prototype.registerAbrToPlayer = function (abrToPlayer) {
        this.abrToPlayer = abrToPlayer;
    };
    BufferAssessment.prototype.addStabilisedBufferChangeListener = function (listener) {
        this.bufferChangeListeners.push(listener);
    };
    BufferAssessment.prototype.removeStabilisedBufferChangeListener = function (listener) {
        var listenerIndex = this.bufferChangeListeners.findIndex(function (functionRef) { return functionRef === listener; });
        if (listenerIndex !== -1) {
            this.bufferChangeListeners.splice(listenerIndex, 1);
        }
    };
    BufferAssessment.prototype.getBufferAssessmentConfig = function () {
        return (0, ts_deepmerge_1.merge)(this.bufferAssessmentConfig, {});
    };
    BufferAssessment.prototype.raiseStabilisedBufferAheadChangeEvent = function (bufferChangeEvent) {
        this.logger.verbose('Buffer change event', bufferChangeEvent);
        this.bufferChangeListeners.forEach(function (f) { return f(bufferChangeEvent); });
    };
    BufferAssessment.prototype.getCurrentBufferAhead = function (bufferAhead) {
        if (bufferAhead > this.prevBufferAhead) {
            this.lowestBufferAhead = this.prevBufferAhead;
        }
        this.prevBufferAhead = bufferAhead;
        return Math.min(this.lowestBufferAhead, bufferAhead);
    };
    BufferAssessment.prototype.getBufferedRangeSeconds = function () {
        var _a;
        var bufferedInfo = (_a = this.abrToPlayer) === null || _a === void 0 ? void 0 : _a.getBufferedInfo();
        var bufferedTotal = bufferedInfo === null || bufferedInfo === void 0 ? void 0 : bufferedInfo.buffer;
        var currentTime = bufferedInfo === null || bufferedInfo === void 0 ? void 0 : bufferedInfo.currentTime;
        if (!bufferedTotal || !bufferedTotal.length || currentTime === undefined) {
            return 0;
        }
        var range = 0;
        var _b = bufferedTotal[bufferedTotal.length - 1], end = _b.end, start = _b.start;
        if (currentTime < end && currentTime >= start) {
            range = end - currentTime;
        }
        return Math.floor(range);
    };
    BufferAssessment.prototype.getStabilisedBufferAhead = function () {
        return this.lastStabilisedBufferAhead;
    };
    BufferAssessment.prototype.refreshBufferState = function (isLowBufferExpected) {
        if (isLowBufferExpected !== undefined) {
            this.isLowBufferExpected = isLowBufferExpected;
        }
        this.setBufferState();
    };
    BufferAssessment.prototype.setBufferState = function () {
        var stabilisedBufferAhead = this.getCurrentBufferAhead(this.getBufferedRangeSeconds());
        var bufferState;
        /**
         * PANIC
         */
        if (stabilisedBufferAhead <= this.bufferAssessmentConfig.bufferThresholdSeconds.rebuffer) {
            bufferState = abr_types_1.AbrHeuristicBufferState.PANIC;
        }
        else if (
        /**
         * REBUFFER
         */
        stabilisedBufferAhead >= this.bufferAssessmentConfig.bufferThresholdSeconds.rebuffer &&
            stabilisedBufferAhead < this.bufferAssessmentConfig.bufferThresholdSeconds.steady) {
            bufferState = abr_types_1.AbrHeuristicBufferState.REBUFFER;
        }
        else {
            /**
             * STEADY
             */
            bufferState = abr_types_1.AbrHeuristicBufferState.STEADY;
        }
        if (bufferState === abr_types_1.AbrHeuristicBufferState.STEADY || bufferState === abr_types_1.AbrHeuristicBufferState.REBUFFER) {
            this.isLowBufferExpected = false;
        }
        if (this.bufferState !== bufferState || this.lastStabilisedBufferAhead !== stabilisedBufferAhead) {
            this.raiseStabilisedBufferAheadChangeEvent({
                bufferState: bufferState,
                bufferAhead: stabilisedBufferAhead,
                isLowBufferExpected: this.isLowBufferExpected,
            });
        }
        this.bufferState = bufferState;
        this.lastStabilisedBufferAhead = stabilisedBufferAhead;
    };
    BufferAssessment.prototype.destroy = function () {
        clearInterval(this.intervalId);
        this.intervalId = undefined;
    };
    BufferAssessment.prototype.bufferRangeStateInterval = function () {
        var _this = this;
        if (this.intervalId) {
            return this.intervalId;
        }
        return setInterval(function () {
            _this.setBufferState();
        }, this.bufferAssessmentConfig.bufferInterval);
    };
    return BufferAssessment;
}());
exports.BufferAssessment = BufferAssessment;
